import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'

import ModalContainer from 'components/ModalContainer'
import { buildTranslate } from 'locales'

import RetryAlert from './RetryAlert'
import SourceModePill from './SourceModePill'
import VersionsTable from './VersionsTable'
import type { SelectionUids } from './versionTypes'

const t = buildTranslate('components.ai.picker.canonical')

const MODAL_WIDTH = '960px'
// Fits the dialog in a phone's width, with a gutter on each side
const MODAL_MAX_WIDTH = 'calc(100vw - 32px)'
// Above the app's fixed header, which sits at 900
const MODAL_Z_INDEX = 1000
// Enough room that a long list is worth scrolling, with the header still in view
const LIST_MAX_HEIGHT = '52vh'

type Props = {
  isOpen: boolean
  isSpecific: boolean
  /** null while loading or after a failure */
  versions: SelectionUids | null
  isError: boolean
  locationNames: ReadonlyMap<number, string>
  locationsFailed: boolean
  isRetryingLocations: boolean
  onRetry: () => void
  onRetryLocations: () => void
  onClose: () => void
}

/** "What's inside": every UID a level's picks stand for */
const SelectionVersionsModal = ({
  isOpen,
  isSpecific,
  versions,
  isError,
  locationNames,
  locationsFailed,
  isRetryingLocations,
  onRetry,
  onRetryLocations,
  onClose,
}: Props) => {
  const [css] = useStyletron()

  const renderBody = () => {
    if (isError) {
      return (
        <div role="alert" className="py-l text-center">
          <Text.BodyText4 tag="p" color="grey70">
            {t('versions.error')}
          </Text.BodyText4>
          <Button kind="minimal" onClick={onRetry}>
            {t('retry')}
          </Button>
        </div>
      )
    }
    return (
      <>
        {!versions && (
          <span role="status" className="sr-only">
            {t('versions.loading')}
          </span>
        )}
        {locationsFailed && (
          <RetryAlert
            message={t('versions.locations_error')}
            disabled={false}
            isLoading={isRetryingLocations}
            onRetry={onRetryLocations}
          />
        )}
        <div className={css({ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' })}>
          <VersionsTable
            uids={versions}
            locationNames={locationNames}
            locationsFailed={locationsFailed}
          />
        </div>
      </>
    )
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      ariaLabel={t('versions.title')}
      width={MODAL_WIDTH}
      dialogStyle={{ maxWidth: MODAL_MAX_WIDTH }}
      zIndex={MODAL_Z_INDEX}
      hideCancel
      proceedText={t('versions.done')}
      onProceed={onClose}
      onClose={onClose}
    >
      <Text.Header3 tag="h2" color="grey90" className="m-0">
        {t('versions.title')}
      </Text.Header3>
      <div className="d-flex align-items-center flex-wrap mt-xs">
        <span className="mr-xs">
          <SourceModePill isSpecific={isSpecific} />
        </span>
        <Text.BodyText4 color="grey70">
          {t(isSpecific ? 'versions.mode_specific' : 'versions.mode_smart')}
        </Text.BodyText4>
      </div>
      <Text.BodyText5 tag="p" color="grey70" className="mt-xs">
        {t('versions.locations_warning')}
      </Text.BodyText5>
      <div className="mt-m">{renderBody()}</div>
    </ModalContainer>
  )
}

export default SelectionVersionsModal
